/**
 * `realtimeKeyMint.ts` — Campfire's ephemeral realtime key mint.
 *
 * Port of Unity's `MintKeyRoutine` + `AIRealtimeKeyMintingConfig`
 * (`AIRealtimeTransportModelRTC.cs`), and shared by BOTH web transports so the
 * request contract cannot drift between them.
 *
 * The whole contract, verified against the dev service 2026-08-28:
 *
 *   POST {campfire_realtime_key_minting_app_url}
 *   X-App-Key: {campfire_minting_app_key}
 *   (no body)
 *   -> 200 { value, expires_at, session: { … } }   // OpenAI client-secret shape
 *   -> 403 {"error":"Forbidden"}                    // no / wrong app key
 *
 * Unity omits the header when the key is blank, which is why a keyless call is
 * a real case rather than a programming error — it 403s.
 *
 * ## Why this matters more than it looks
 *
 * The response is a **new-style client secret** (`/v1/realtime/client_secrets`
 * shape), and the minted key is accepted by
 * `POST https://api.openai.com/v1/realtime/calls` — verified. That is what lets
 * the WebRTC transport do its SDP exchange BROWSER-DIRECT, with no proxy and no
 * server-side OpenAI key, on any origin:
 *
 *   - the mint service reflects the caller's `Origin` in
 *     `Access-Control-Allow-Origin` (checked for both `http://localhost:5173`
 *     and `https://a1.campfire3d.com`), and allows the `X-App-Key` header;
 *   - `api.openai.com/v1/realtime/calls` answers `Access-Control-Allow-Origin: *`.
 *
 * ⚠ `session.model` comes back EMPTY, so the mint service does not pin a model
 * — the caller's `?model=` on the calls URL is what chooses it. Do not assume a
 * minted key locks the session to anything; its `session` block is OpenAI's own
 * boilerplate (voice `alloy`, stock instructions) and the first `session.update`
 * replaces all of it.
 *
 * The app key is a NARROW secret: its only power is to mint short-lived realtime
 * tokens, it is origin-checked by the mint service, and Unity ships it in its
 * own app config. It is deliberately committed (direction call, 2026-08-28) —
 * unlike the OpenAI API key, which must never reach a browser.
 */

import { getAiConfig } from "@ai/aiConfig";

/** Unity's `keyRequest.timeout = 10`. */
const K_MINT_TIMEOUT_MS = 10_000;

/**
 * Mints one ephemeral realtime key, or returns null.
 *
 * Never throws: every caller is on a connect path where the only useful
 * response to a failure is to try the next option.
 */
export async function mintRealtimeEphemeralKeyAsync(): Promise<string | null> {
  const config = getAiConfig();
  if (!config.realtimeKeyMintingUrl || !config.mintingAppKey) return null;
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), K_MINT_TIMEOUT_MS);
  try {
    const response = await fetch(config.realtimeKeyMintingUrl, {
      method: "POST",
      headers: { "X-App-Key": config.mintingAppKey },
      signal: abort.signal,
    });
    if (!response.ok) {
      console.warn(`[realtimeKeyMint] mint refused (${response.status})`);
      return null;
    }
    const body = (await response.json()) as Record<string, unknown>;
    return extractEphemeralKey(body);
  } catch (error) {
    console.warn("[realtimeKeyMint] mint failed:", error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Port of `OpenAIRealtimeUtils.TryExtractEphemeralKey`.
 *
 * Three accepted shapes because the mint service has carried all of them:
 * `client_secret.value` (the old `/v1/realtime/sessions` response), and the
 * bare `value` / `key` the current service returns.
 */
export function extractEphemeralKey(body: Record<string, unknown>): string | null {
  const clientSecret = body.client_secret;
  if (typeof clientSecret === "object" && clientSecret !== null) {
    const value = (clientSecret as Record<string, unknown>).value;
    if (typeof value === "string" && value) return value;
  }
  if (typeof body.value === "string" && body.value) return body.value;
  if (typeof body.key === "string" && body.key) return body.key;
  return null;
}
